const createArticles = (items) =>
  items.map((item) => ({
    ...item,
    body: item.body || [
      `Open the ${item.title.toLowerCase()} workflow from this screen.`,
      "Review the available information, make the required changes, and save before leaving the page.",
    ],
  }));

const defaultTour = [
  {
    title: "Navigate this page",
    description: "Use the page navigation to move between PIERS work areas.",
    target: "#page-topbar",
    placement: "bottom",
  },
  {
    title: "Work with the page",
    description:
      "The page content stays in place while you review, search, filter, and update information.",
    target: ".main-content",
    placement: "left",
  },
];

// The full Help Center uses these topic groups. Keep the content close to the
// screens it documents so the contextual drawer and the standalone Help Center
// always describe the same workflow.
export const HELP_CENTER_TOPICS = [
  {
    id: "firm-administration",
    icon: "shield",
    title: "Firm administration",
    description:
      "Set up the firm, branches, users, roles, and access controls that support PIERS work.",
    articles: createArticles([
      {
        id: "firm-admin-overview",
        group: "Firm administration",
        title: "Understand the Firm Admin workspace",
        body: [
          "Firm administrators use the Firm Admin Dashboard to monitor firm activity, engagement status, user adoption, deadlines, and operational actions. Use its cards and links to open the related administration workspace; it is a monitoring and navigation screen, not the source of record for user or firm changes.",
          "The administration journey is: maintain the firm profile and branches, create or update users, assign each user a primary role and branch, then review the resulting access and activity from the dashboard. A user can only be created when the required identity, contact, branch, role, and password information is present.",
        ],
      },
      {
        id: "firm-profile-and-branches",
        group: "Firm setup",
        title: "Create or update a firm profile and branches",
        body: [
          "Open the firm list to add a firm or edit an existing one. The profile captures firm name, address, contact number, email address, and website. A firm name is required; email must be a valid email address when supplied, website must be a valid URL when supplied, and the address is limited to 500 characters.",
          "Saving sends the profile to the company add/update endpoint. After the firm exists, maintain its branches or locations from the firm detail area. Those locations become the branch choices used when users are created or updated, and the firm can then be selected for engagements and inspection workflows.",
        ],
      },
      {
        id: "firm-user-lifecycle",
        group: "Users and access",
        title: "Create, update, and secure users",
        body: [
          "Open Settings > Security > Users to search users by name, login, role, branch, or designation, then filter by role and active or inactive status. The table shows the user's primary role, branch, designation, email/login, last login, status, and actions to edit the user or reset the password.",
          "For a new user, provide full name, email login, mobile number, branch, designation, primary role, password, and matching confirmation. System-level administrators also choose the firm; choosing a firm refreshes the branch list for that firm. For an existing user, the account controls can disable the user, block sign-in, or require a password change at the next login.",
          "The form first loads the signed-in user, available firms, locations, roles, and—when editing—the user record. It submits to the user add or update endpoint only after client-side validation passes. Reset password is a separate action, so editing profile data does not itself replace a password.",
        ],
      },
      {
        id: "firm-roles-and-governance",
        group: "Users and access",
        title: "Review roles and access governance",
        body: [
          "The Roles tab lists the configured role name, description, and last-updated date. Use its search to locate a role before assigning it to a user. The current screen is a review and selection surface; it does not create or edit role definitions from this page.",
          "Apply least privilege: select the primary role that is required for the user's work, verify the firm and branch are correct, and use the account controls when access must be suspended. Because roles are loaded as shared reference data, a role change affects the permissions represented by that role wherever it is assigned.",
        ],
      },
      {
        id: "firm-admin-data-flow",
        group: "Application and data flow",
        title: "Follow the Firm Admin application and data flow",
        body: [
          "The Security screen loads user and role summaries in parallel, then each tab retrieves its own list for filtering and display. The user form loads its lookup data before it is saved: signed-in user's firm context, firms, locations, roles, and the selected user when editing. Changing the selected firm reloads its locations and clears the branch selection so a branch cannot be saved against the wrong firm.",
          "On save, the React form validates and posts a compact request model to the PIERS API. The application receives a success or error response, shows feedback, closes the drawer on success, and refreshes the user list. Firm profile changes follow the same request-response pattern through the company endpoint. Treat the API response as the source of truth; the dashboard and grids are refreshed views of that data.",
        ],
      },
    ]),
  },
  {
    id: "inspection-workspace",
    icon: "check-square",
    title: "Inspection workspace",
    description:
      "Plan, assign, complete, review, and track inspections and their supporting evidence.",
    articles: createArticles([
      {
        id: "inspection-list-and-status",
        group: "Inspection workflow",
        title: "Start from the inspection review list",
        body: [
          "The Engagement Review list loads inspection records and lets users narrow them by ownership, status, assignee, due date, and inspection type. Each row presents the current status, percentage, due-date state, assigned people, and the action that matches the status—for example Start Review, Resume Review, View Queries, Review Findings, or Open Review.",
          "Use the row actions for the review summary, review assignment, due-date change, activity history, or cancellation. Status and percentage determine the primary action label. A completed inspection is opened for review; an inspection in progress resumes the review workspace; an inspection awaiting queries directs the user to the outstanding questions.",
        ],
      },
      {
        id: "inspection-assignment",
        group: "Inspection workflow",
        title: "Assign the inspection review team",
        body: [
          "Open Review Assignment from an inspection row to set the review owner, primary reviewer, additional reviewers, approver, and optional observer. The owner defaults from the engagement manager, the primary reviewer defaults from the assigned or senior reviewer, and the approver defaults from the engagement partner. Assignment applies to the selected review only; it does not alter the engagement team.",
          "Saving requires a review owner, primary reviewer, a real change, and a reason. When the reason is Other, an explanation is required. If the primary reviewer changes while the inspection is in progress, PIERS asks for confirmation and preserves existing responses, queries, documents, and progress. The notification setting controls whether affected team members are notified.",
          "The assignment experience currently updates the review record held by the list screen for the active session. Persisting an assignment change to the API should be added before relying on it as an audit record across refreshes or sessions.",
        ],
      },
      {
        id: "inspection-workspace-overview",
        group: "Inspection workspace screens",
        title: "Use the inspection workspace overview and tabs",
        body: [
          "Opening a review displays its engagement, client, inspection type, round, due date, progress, completed sections, open queries, findings, attachments, review team, and recent activity. The overview is designed to answer what is due, what is blocked, and who is responsible before a reviewer opens the checklist.",
          "Use the tabs to move between Overview, Checklist Sections, Queries, Findings, Documents, Team, and Activity Timeline. Quick Start resumes the checklist, opens queries, or takes the user to evidence. Review Actions provides team management, activity, and workspace closing. The workspace shows the review's live checklist, query, finding, evidence, team, and activity data; empty states are shown when no records exist.",
        ],
      },
      {
        id: "inspection-checklist-flow",
        group: "Inspection workspace screens",
        title: "Complete a checklist question",
        body: [
          "From Checklist Sections or Resume Review, choose a checklist to open the full-screen checklist review. The left navigator searches questions, shows their response state, and tracks section progress. The center screen shows the selected question, response options (Yes, No, Not Applicable, or Needs Follow-up), reviewer conclusion, evidence, and previous/next navigation. The right context panel contains question-level evidence, queries, discussions, and history.",
          "Question definitions are requested from the checklist question API using the selected checklist ID. Selecting an answer or editing the conclusion marks the current question as unsaved. Save records the interaction in the current workspace and advances only when the reviewer chooses Next Question. Attachments can be added from the user's device, and an existing workpaper can be linked from the evidence section.",
          "Important current boundary: response, conclusion, discussion, upload list, query state, and progress in this checklist screen are managed in browser state; Save currently shows confirmation without posting a review-response payload. The checklist definition is loaded from the API, but durable response, evidence, query, finding, and audit-history endpoints are still required for these actions to survive a refresh.",
        ],
      },
      {
        id: "inspection-queries-findings-evidence",
        group: "Inspection workflow",
        title: "Manage queries, findings, evidence, and discussion",
        body: [
          "Use Queries to identify work that requires a response and view the question, raising user, date, priority, and status. Findings identify matters recorded during the review. Documents and Evidence gathers the files supporting each answer, while the question context panel keeps evidence, queries, discussions, and history attached to the selected question.",
          "The intended business flow is: record an answer, attach or link evidence, raise a query or finding when follow-up is needed, discuss and resolve the item, then complete the section and submit the review for approval. The progress metric should be derived from completed questions, while open queries and findings remain visible as controls before approval. Current count and activity examples in the workspace are presentation data until their matching API persistence is connected.",
        ],
      },
      {
        id: "inspection-data-flow",
        group: "Application and data flow",
        title: "Follow the inspection application and data flow",
        body: [
          "The review list obtains inspection records from the inspection list API, derives display status, due-date state, risk, progress, and the primary action in the client, and passes the selected record into the workspace. The workspace then loads available checklists. Opening a checklist loads its question definitions using that checklist ID and uses the selected review data to display engagement and client context.",
          "At present, the persisted inbound flow is inspection list -> selected review -> checklist list -> checklist questions. Most interaction after that point is local React state. To complete the production data flow, the save actions need review-specific APIs for responses and conclusions, evidence upload/link metadata, queries and findings, review assignments, activity events, calculated progress, and final approval. Those APIs should enforce role access and preserve an immutable audit trail.",
        ],
      },
    ]),
  },
];

const firmAdministrationArticles = HELP_CENTER_TOPICS.find(
  (topic) => topic.id === "firm-administration"
).articles;
const inspectionWorkspaceArticles = HELP_CENTER_TOPICS.find(
  (topic) => topic.id === "inspection-workspace"
).articles;

const defaultContext = {
  id: "piers.general",
  icon: "bx-help-circle",
  title: "PIERS Help",
  description: "Guidance for the PIERS screen you are working on.",
  about:
    "Use this panel for concise guidance about the current screen. Help opens over your work so filters, selections, and form values remain unchanged.",
  capabilities: [
    "Understand the current screen",
    "Find the next task",
    "Start a guided tour",
  ],
  articles: createArticles([
    {
      id: "getting-started",
      group: "Getting started",
      title: "Use PIERS Help",
      body: [
        "Select the Help icon in the header at any time.",
        "Choose Overview for a quick explanation or How-to for task-based guidance.",
      ],
    },
  ]),
  tourSteps: defaultTour,
};

const helpCenterContext = {
  id: "piers.help-center",
  title: "PIERS Help Center",
  description: "Broader guidance for working in PIERS.",
  articles: createArticles([
    {
      id: "help-center-search",
      group: "Help Center",
      title: "Search the PIERS Help Center",
      body: [
        "Use the Help Center search to find guidance across PIERS, not only for the page currently open.",
        "Open the full Help Center in a new tab when you need to browse all available topics.",
      ],
    },
    {
      id: "guided-tour",
      group: "Help Center",
      title: "Use a guided tour",
      body: [
        "Guided tours highlight relevant parts of the current screen step by step.",
        "Choose Skip Tour at any time to return to your work.",
      ],
    },
    {
      id: "support",
      group: "Help Center",
      title: "Get additional support",
      body: [
        "If the available guidance does not answer your question, use your firm’s usual support process.",
      ],
    },
  ]),
};

export const HELP_CONTEXTS = {
  "firm.dashboard": {
    id: "firm.dashboard",
    icon: "bx-grid-alt",
    title: "Firm Admin Dashboard",
    description:
      "Monitor your firm’s work, users, activity, and operational status.",
    about:
      "The dashboard is the starting point for firm administration. It summarizes activity and links to the firm profile, locations, security, users, and roles where the underlying records are maintained.",
    capabilities: [
      "Review firm activity",
      "Maintain firm and branch data",
      "Create and secure users",
      "Review roles and access data",
      "Understand the administration data flow",
    ],
    articles: firmAdministrationArticles,
    tourSteps: defaultTour,
  },
  "admin.checklists": {
    id: "admin.checklists",
    icon: "bx-list-check",
    title: "Checklists",
    description:
      "Manage inspection checklists, their questions, areas, and topics.",
    about:
      "Checklists provide the questions and structure used in review work. From this screen you can create, edit, filter, view questions, and remove a checklist only when it is not used by an engagement or inspection.",
    capabilities: [
      "Create a checklist",
      "Edit checklist details",
      "View and manage questions",
      "Search and filter checklists",
      "Delete an unused checklist",
    ],
    articles: createArticles([
      {
        id: "create-checklist",
        group: "Getting started",
        title: "Create a checklist",
      },
      {
        id: "edit-checklist",
        group: "Managing checklists",
        title: "Edit a checklist",
      },
      {
        id: "questions",
        group: "Managing checklists",
        title: "View checklist questions",
      },
      {
        id: "delete-checklist",
        group: "Managing checklists",
        title: "Delete an unused checklist",
        body: [
          "Open the checklist Actions menu and choose Delete checklist when the option is available.",
          "A checklist cannot be deleted once it is associated with an engagement or inspection.",
        ],
      },
      {
        id: "filter-checklists",
        group: "Managing checklists",
        title: "Search and filter checklists",
      },
    ]),
    tourSteps: [
      ...defaultTour,
      {
        title: "Filter checklists",
        description:
          "Use search and filters to narrow the checklist list without losing your current work.",
        target: ".standards-filter-panel",
        placement: "bottom",
      },
      {
        title: "Open checklist actions",
        description:
          "Use the Actions menu to edit a checklist, view its questions, or delete an eligible checklist.",
        target: ".standards-grid-panel",
        placement: "top",
      },
    ],
  },
  "admin.references": {
    id: "admin.references",
    icon: "bx-book-open",
    title: "Reference Data",
    description:
      "Manage reusable task types, work types, client types, and other reference values.",
    about:
      "Reference data standardizes the choices available across PIERS. Keep values clear and current so they can be used consistently in engagements, reviews, and reports.",
    capabilities: [
      "Create reference values",
      "Edit existing values",
      "Search and filter records",
      "Review status information",
    ],
    articles: createArticles([
      {
        id: "create-reference",
        group: "Getting started",
        title: "Create a reference value",
      },
      {
        id: "edit-reference",
        group: "Managing reference data",
        title: "Edit a reference value",
      },
      {
        id: "filter-reference",
        group: "Managing reference data",
        title: "Search and filter reference data",
      },
    ]),
    tourSteps: defaultTour,
  },
  "admin.templates.review-template": {
    id: "admin.templates.review-template",
    icon: "bx-file",
    title: "Review Templates",
    description: "Create and maintain reusable templates for review workflows.",
    about:
      "Review templates define the workflow, checklists, questions, and report formats used by review engagements. Preview a template to inspect it without changing its configuration.",
    capabilities: [
      "Create a template",
      "Preview template configuration",
      "Edit a template",
      "Publish a completed template",
    ],
    articles: createArticles([
      {
        id: "create-template",
        group: "Getting started",
        title: "Create a review template",
      },
      {
        id: "preview-template",
        group: "Managing templates",
        title: "Preview a template",
      },
      {
        id: "edit-template",
        group: "Managing templates",
        title: "Edit a template",
      },
      {
        id: "publish-template",
        group: "Managing templates",
        title: "Publish a template",
      },
    ]),
    tourSteps: defaultTour,
  },
  "engagement.details": {
    id: "engagement.details",
    icon: "bx-briefcase",
    title: "Engagement Setup",
    description: "Configure an engagement through six guided steps.",
    about:
      "The engagement setup workspace separates identification, risk, scope, checklists, documents, and scheduling into six steps. Save as Draft preserves incomplete work; Next Step saves the current draft before checking the fields required to continue.",
    capabilities: [
      "Enter basic information",
      "Assess risks",
      "Configure review types and a template",
      "Assign checklists",
      "Manage workpapers",
      "Assign the team and schedule",
    ],
    articles: createArticles([
      {
        id: "engagement-workflow",
        group: "Setting up an engagement",
        title: "Follow the six engagement steps",
        body: [
          "Complete the workspace in this order: Basic Details, Risk Assessment, Services & Scope, Checklists, Documents & Workpapers, then Team & Dates.",
          "Use Save as Draft at any step to retain incomplete work. Use Previous and Next Step to move through the workflow; the final step changes the action to Review & Save or Review & Create.",
        ],
      },
      {
        id: "engagement-basic-information",
        group: "Setting up an engagement",
        title: "Complete Basic Details",
        body: [
          "Enter Engagement Name and Financial Year End on the first row. On the second row, select the Inspection Cycle and Client. Client Type and Industry Type are filled from the selected client, and Work Type completes the final row.",
          "All seven fields on this step are required. Selecting a different client refreshes its client type and industry.",
        ],
      },
      {
        id: "engagement-review-setup",
        group: "Review configuration",
        title: "Configure scope, template, and checklists",
        body: [
          "In Services & Scope, add the review types first and define the scope, exclusions, and sign-off requirements for each one. Then select and preview the review template that applies to those review types.",
          "In Checklists, choose the checklists for each configured review type and assign the reviewer, checklist sign-off user, and any additional review levels supplied by the template.",
        ],
      },
      {
        id: "engagement-documents-team-dates",
        group: "Completing setup",
        title: "Add workpapers, team members, and dates",
        body: [
          "Documents & Workpapers becomes available after the engagement has a saved draft ID. Upload supporting files and organize them in the configured folders.",
          "The final Team & Dates step assigns the engagement partner, engagement manager, senior reviewer, optional additional reviewers and observers, plus the inspection start, due, expected completion, and optional planning meeting dates.",
        ],
      },
    ]),
    tourSteps: defaultTour,
  },
  "engagement.reviews": {
    id: "engagement.reviews",
    icon: "bx-clipboard",
    title: "Inspection Reviews",
    description:
      "Monitor review ownership, workflow progress, sign-off, and follow-up work.",
    about:
      "Inspection Reviews is the firm-wide work queue for inspection and review engagements. Use it to identify the current owner, review stage, due date, progress, risk level, and outstanding workflow work before opening a review.",
    capabilities: [
      "Filter and prioritize the review queue",
      "See the current workflow assignee and action",
      "Expand pending checklist and sign-off work",
      "Open or resume a review",
      "Review assignment and activity history",
    ],
    articles: createArticles([
      {
        id: "review-list-filters",
        group: "Finding review work",
        title: "Filter and prioritize engagement reviews",
        body: [
          "Use View to switch between all reviews and reviews assigned to you. Combine Status, Assigned To, Review Type, and Due Date to focus the grid on a particular queue. Reset clears all filter selections.",
          "The summary cards and quick filters are shortcuts: My Work and Assigned to Me focus on your workload; Awaiting Queries and In Review identify blocked or active work; Overdue and Completed highlight exceptions and finished reviews.",
        ],
      },
      {
        id: "review-grid-columns",
        group: "Reading the review grid",
        title: "Understand the review grid columns",
        body: [
          "Review Job ID and Engagement identify the review record and its client engagement. Review Type and Service Type describe the work being performed, while Review Template and Round show the template version and review cycle used for that review.",
          "Assigned To and Current Action show who owns the active workflow stage. Multiple avatar badges mean more than one active participant; the supporting text identifies the current activity, such as checklist review, checklist sign-off, or final sign-off.",
          "Due Date and Created On help prioritize work. Status and Progress show the overall review stage and completion percentage. Engagement Risk Level is calculated from the risks selected on the engagement.",
        ],
      },
      {
        id: "review-workflow-tree",
        group: "Reading the review grid",
        title: "View pending checklist and sign-off work",
        body: [
          "Select the chevron at the start of a review row to expand its pending work. The indented group rows separate Checklist work from Final sign-off, and their child rows identify the exact task, assigned role or user, current task status, and last update.",
          "These subrows explain the review workflow but are not separate engagement reviews. Sorting and pagination continue to apply only to the parent review rows.",
        ],
      },
      {
        id: "review-row-actions",
        group: "Working with reviews",
        title: "Use the Actions menu",
        body: [
          "Open the three-dot Actions menu at the end of a parent review row. Review Summary opens the complete engagement-review workspace. The primary action starts or resumes the review for the currently assigned user; it is unavailable to other users.",
          "Users with management permission may see Review Assignment and Change Due Date when the workflow allows it. Activity History shows the audit trail. Cancel Review is restricted to authorized users and should be used only when the review is no longer required.",
        ],
      },
      {
        id: "review-status-and-signoff",
        group: "Working with reviews",
        title: "Understand status, sign-off, and rework",
        body: [
          "A checklist moves from review to its configured checklist sign-off users, then to any final sign-off users. Active means a task is ready for its assignee; Pending means an earlier workflow step must finish first.",
          "A sign-off user can add comments to an individual question. If correction is needed, Re-open for Review returns that question and its checklist task to the original reviewer with the recorded feedback.",
        ],
      },
    ]),
    tourSteps: [
      {
        title: "Review workload at a glance",
        description:
          "These cards summarize all reviews, reviews awaiting assignment, active work, completed reviews, and overdue items. Select a card to apply its related queue filter.",
        target: ".engagement-review-summary",
        placement: "bottom",
      },
      {
        title: "Narrow the queue",
        description:
          "Use View, Status, Assigned To, Review Type, and Due Date together to find the work you need. Reset restores the full current queue.",
        target: ".engagement-review-filter-panel",
        placement: "bottom",
      },
      {
        title: "Use quick work queues",
        description:
          "My Work and Assigned to Me focus on your tasks. Awaiting Queries, In Review, Overdue, and Completed provide fast operational views without changing the underlying review records.",
        target: ".engagement-review-quick-filters",
        placement: "bottom",
      },
      {
        title: "Identify the review",
        description:
          "The first column contains the Review Job ID and its Engagement. Use the chevron before the row to expand pending workflow tasks for that review.",
        target: '.engagement-review-grid [data-field="reviewJob"]',
        placement: "bottom",
      },
      {
        title: "Understand the review definition",
        description:
          "Review Type/Service Type tells you what work is being performed. Review Template/Round identifies the template and review cycle governing it.",
        target: '.engagement-review-grid [data-field="reviewType"]',
        placement: "bottom",
      },
      {
        title: "Check ownership and timing",
        description:
          "Assigned To/Current Action identifies the active workflow owner. Due Date/Created On helps you prioritize the review and understand its age.",
        target: '.engagement-review-grid [data-field="assignedTo"]',
        placement: "bottom",
      },
      {
        title: "Read stage, progress, and risk",
        description:
          "Status/Progress shows the overall review stage and completion percentage. Engagement Risk Level is derived from the risks selected for the engagement.",
        target: '.engagement-review-grid [data-field="status"]',
        placement: "bottom",
      },
      {
        title: "Expand pending workflow work",
        description:
          "Use this chevron to reveal Checklist work and Final sign-off groups. The child rows show each pending task, its assignee, status, and last update; they do not affect parent-row pagination or sorting.",
        target: ".engagement-review-tree-toggle",
        placement: "right",
        onHighlightStarted: (element) => {
          if (element?.getAttribute("aria-expanded") !== "true")
            element.click();
        },
      },
      {
        title: "Open a review action",
        description:
          "Use the three-dot menu for Review Summary, the available Start or Resume action, Activity History, and—when you have permission—assignment, due-date, or cancellation actions.",
        target: ".engagement-review-action-trigger",
        placement: "left",
        onHighlightStarted: (element) => {
          if (element?.getAttribute("aria-expanded") !== "true")
            element.click();
        },
        onDeselected: (element) => {
          if (element?.getAttribute("aria-expanded") === "true")
            element.click();
        },
      },
    ],
  },
  "engagement.list": {
    id: "engagement.list",
    icon: "bx-briefcase",
    title: "Engagements",
    description:
      "Find, filter, create, import, and manage inspection engagements.",
    about:
      "The Engagements list brings together the current engagement workload. Use the filters and status views to narrow the list, then use each row's Actions menu to continue the appropriate workflow.",
    capabilities: [
      "Filter engagements",
      "Use status views",
      "Import engagements",
      "Create an engagement",
      "Manage an engagement from its Actions menu",
    ],
    articles: createArticles([
      {
        id: "filter-engagements",
        group: "Finding engagements",
        title: "Filter the engagement list",
        body: [
          "Use View, Status, Assigned To, Due, and Work Type together to narrow the engagement list. Select Reset to return to the full list.",
          "The status-view buttons below the filters are quick shortcuts for common queues, such as My Work, In Progress, In Review, and Completed.",
        ],
      },
      {
        id: "import-engagements",
        group: "Getting started",
        title: "Import engagements",
        body: [
          "Select Import Engagements to load engagements from an approved workbook. Review and resolve any validation issues before creating the imported records.",
        ],
      },
      {
        id: "create-engagement",
        group: "Getting started",
        title: "Create an engagement",
        body: [
          "Select New Engagement to open the six-step setup: Basic Details, Risk Assessment, Services & Scope, Checklists, Documents & Workpapers, and Team & Dates.",
          "Basic Details captures the engagement name, financial year, inspection cycle, client, client classifications, and work type. Review types are configured before choosing the review template, while team assignments and dates are completed in the final step.",
        ],
      },
      {
        id: "engagement-row-actions",
        group: "Managing engagements",
        title: "Use an engagement's Actions menu",
        body: [
          "Open the three-dot Actions menu at the end of an engagement row to view activities and, when available for your role and the engagement's status, edit it, assign data preparation, send it for review, or delete it.",
        ],
      },
    ]),
    tourSteps: [
      {
        title: "Engagement overview",
        description:
          "These cards summarize the current workload by lifecycle stage and upcoming due dates.",
        target: ".engagement-summary-row",
        placement: "bottom",
      },
      {
        title: "Import engagements",
        description:
          "Use Import Engagements to bring in approved workbook data and resolve any validation issues before creating the records.",
        target: ".import-engagements",
        placement: "bottom",
      },
      {
        title: "Create a new engagement",
        description:
          "Select New Engagement to open the six-step setup. Start with engagement, inspection-cycle, client, and work details; team assignments and dates are completed in the final step.",
        target: ".create-engagement",
        placement: "bottom",
      },
      {
        title: "Filter engagements",
        description:
          "Narrow the list by view, status, assignee, due date, and work type. Use Reset to clear every filter and return to the full list.",
        target: ".engagement-filter-panel",
        placement: "bottom",
      },
      {
        title: "Use status views",
        description:
          "These quick views switch between common work queues, including My Work, In Progress, In Review, and Completed.",
        target: ".engagement-quick-filters",
        placement: "bottom",
      },
      {
        title: "Review the engagement list",
        description:
          "Each row shows the engagement's client, work type, owner, status, progress, risk level, due date, and last update.",
        target: ".engagement-grid-wrap",
        placement: "top",
      },
      {
        title: "Open the Actions menu",
        description:
          "Use the three-dot menu for row-specific actions. Available choices depend on your role and the engagement's current status.",
        target: ".engagement-list__tour-actions",
        placement: "left",
        onHighlightStarted: (element) => {
          if (element?.getAttribute("aria-expanded") !== "true")
            element.click();
        },
        onDeselected: (element) => {
          if (element?.getAttribute("aria-expanded") === "true")
            element.click();
        },
      },
    ],
  },
  "data.import-engagements": {
    id: "data.import-engagements",
    icon: "bx-import",
    title: "Import Engagements",
    description:
      "Upload an engagement workbook, resolve imported data, and create PIERS engagements.",
    about:
      "Use this two-step guided import to create engagement records from an approved workbook. Choose the matching import template and upload the source file, then validate the rows, resolve any issues, and create the ready engagements.",
    capabilities: [
      "Choose an engagement import template",
      "Upload an approved workbook",
      "Resolve client and template issues",
      "Review imported engagement data",
      "Create ready engagements",
    ],
    articles: createArticles([
      {
        id: "choose-engagement-import-template",
        group: "Prepare the import",
        title: "Choose an engagement import template",
        body: [
          "Select the active import definition that matches the workbook you received. The selected template defines the accepted file type, worksheet structure, and the columns PIERS expects.",
          "Use Download Template when you need a new copy of the supported workbook. View Template Details shows the structure and mappings without leaving the import flow.",
        ],
      },
      {
        id: "upload-engagement-workbook",
        group: "Prepare the import",
        title: "Upload an engagement workbook",
        body: [
          "Choose or drag an Excel workbook into the upload area. The file must match the selected import definition and use one of its accepted extensions.",
          "After validation, PIERS reads the supplied rows and takes you to Validate & Create so that client matching, templates, and required engagement fields can be checked before creation.",
        ],
      },
      {
        id: "resolve-engagement-import-issues",
        group: "Review and configure",
        title: "Resolve client and template issues",
        body: [
          "Open a row that needs attention to resolve a missing or conflicting client, select the review template to apply, and verify the engagement information before it is created.",
          "Rows with blocking errors cannot be created. Warnings highlight optional information that should be reviewed before continuing.",
        ],
      },
      {
        id: "create-imported-engagements",
        group: "Complete the import",
        title: "Validate and create engagements",
        body: [
          "Review the included engagements and resolve every blocking validation issue. Excluded rows are skipped.",
          "Choose Create Engagements from Validate & Create only after all required items are ready. Keep the page open while PIERS creates the records and displays the completed import report.",
        ],
      },
    ]),
    tourSteps: [
      {
        title: "Import progress",
        description:
          "These steps show where you are in the import workflow. Completed steps can be revisited when you need to make a change.",
        target: ".engagement-import__stepper",
        placement: "bottom",
      },
      {
        title: "Select the import template",
        description:
          "Choose the active template that matches your workbook before uploading the source file.",
        target: ".engagement-import__template-upload",
        placement: "bottom",
      },
      {
        title: "Upload the workbook",
        description:
          "Drag in the approved Excel workbook or browse for it here. PIERS validates the file before you can continue.",
        target: ".engagement-import__dropzone",
        placement: "top",
      },
      {
        title: "Review imported engagements",
        description:
          "Use this workspace to inspect imported rows and resolve any client, review-template, or validation issues.",
        target: ".engagement-import__workspace",
        placement: "top",
      },
      {
        title: "Fix a row from its action menu",
        description:
          "Open a row's action menu to select Fix/Edit Data. This opens the correction panel, where you can resolve that row's client, review template, and validation issues.",
        target: ".engagement-import__row-action-menu",
        placement: "left",
        onHighlightStarted: (element) => element?.click(),
      },
    ],
  },
  "review.workspace": {
    id: "review.workspace",
    icon: "bx-check-shield",
    title: "Review Workspace",
    description:
      "Complete review work, record findings, and manage review progress.",
    about:
      "The review workspace brings together the inspection list, assignment, checklist questions, evidence, findings, queries, and workflow controls. It also explains which information is already API-backed and which interactions still require persistence endpoints.",
    capabilities: [
      "Open and prioritize inspections",
      "Assign the review team",
      "Answer checklist questions",
      "Manage evidence, findings, and queries",
      "Understand the current inspection data flow",
    ],
    articles: inspectionWorkspaceArticles,
    tourSteps: defaultTour,
  },
  "review.checklist": {
    id: "review.checklist",
    icon: "bx-list-check",
    title: "Checklist Review",
    description:
      "Answer review questions, document conclusions, and manage question-level follow-up.",
    about:
      "Use this workspace to review each checklist question, select the appropriate response, record the reviewer conclusion, and keep supporting evidence and follow-up actions together. A No response requires reviewer comments before it can be saved or advanced.",
    capabilities: [
      "Navigate and complete checklist questions",
      "Record Yes, No, or Not Applicable responses",
      "Document reviewer conclusions",
      "Attach evidence and raise findings",
      "Assign and track review queries",
      "View review activities and history",
    ],
    articles: createArticles([
      {
        id: "review-answer-question",
        group: "Completing the review",
        title: "Answer a checklist question",
        body: [
          "Use the left checklist to select a question, then choose Yes, No, or Not Applicable in the response area. The question reference and text are shown at the top of the review pane.",
          "A response is not complete until it is saved. When you select No, enter reviewer comments before saving or moving to the next question.",
        ],
      },
      {
        id: "review-conclusion-evidence",
        group: "Completing the review",
        title: "Record a conclusion and supporting evidence",
        body: [
          "Use Reviewer Conclusion to document the rationale for the response. Keep the explanation concise and specific enough for another reviewer to understand the decision.",
          "Use Evidence to upload a file or link workpapers that support the answer. Evidence remains associated with the current review question.",
        ],
      },
      {
        id: "review-queries-findings-discussions",
        group: "Managing follow-up",
        title: "Raise and manage follow-up items",
        body: [
          "Use Queries when information or clarification is needed. Provide a subject, details, priority, and an assignee so someone is accountable for responding.",
          "Use Findings to record review matters and Discussions to collaborate on the current question. Resolve follow-up items before finalizing the review.",
        ],
      },
      {
        id: "review-activities-history",
        group: "Tracking progress",
        title: "View review activities and history",
        body: [
          "Use View activities & history in the engagement header to review recorded activity for the review. This helps confirm what changed and when.",
          "The progress indicator reflects completed checklist questions. Use Previous Question and Next Question to move through the review in order.",
        ],
      },
    ]),
    tourSteps: [
      {
        title: "Navigate checklist questions",
        description:
          "Use the checklist on the left to select a question. The status icons and section progress show which questions still need attention.",
        target: ".checklist-review__navigator",
        placement: "right",
      },
      {
        title: "Track your place",
        description:
          "This header shows the active checklist section and your current question number. Use it to stay oriented as you work through the review.",
        target: ".checklist-review__main-progress",
        placement: "bottom",
      },
      {
        title: "Read the question",
        description:
          "Review the question and any reference information before recording an assessment. You can bookmark a question to return to it later.",
        target: ".checklist-review__question-card",
        placement: "bottom",
      },
      {
        title: "Choose an assessment",
        description:
          "Select Yes, No, or Not Applicable for the current question. Your selection is not sent to the server until you save it.",
        target: ".checklist-review__response",
        placement: "bottom",
      },
      {
        title: "Explain your conclusion",
        description:
          "Document the basis for your assessment here. Comments are required whenever you select No, so enter them before saving or moving to the next question.",
        target: ".checklist-review__conclusion",
        placement: "top",
      },
      {
        title: "Add supporting evidence",
        description:
          "Upload a file, link a workpaper, or raise a finding when it supports the current assessment. Evidence stays attached to this question.",
        target: ".checklist-review__evidence",
        placement: "top",
      },
      {
        title: "Manage follow-up",
        description:
          "Use these buttons to work with evidence, queries, findings, and discussions for the current question. Resolve follow-up items before finalizing the review.",
        target: ".checklist-review__context-actions",
        placement: "bottom",
      },
      {
        title: "Save and continue",
        description:
          "Save records the response for this question. Next Question saves first, then opens the next question; use Previous Question to revisit an earlier response.",
        target: ".checklist-review__main-footer",
        placement: "top",
      },
    ],
  },
  "reports.engagement-report": {
    id: "reports.engagement-report",
    icon: "bx-file-find",
    title: "Reports",
    description: "Review, generate, and manage PIERS reports.",
    about:
      "Reports bring together current engagement and review information into a shareable format. Use the available report actions to preview or generate the output you need.",
    capabilities: [
      "Review report availability",
      "Preview report content",
      "Generate reports",
      "Track report status",
    ],
    articles: createArticles([
      {
        id: "preview-report",
        group: "Working with reports",
        title: "Preview a report",
      },
      {
        id: "generate-report",
        group: "Working with reports",
        title: "Generate a report",
      },
    ]),
    tourSteps: defaultTour,
  },
  "settings.security": {
    id: "settings.security",
    icon: "bx-shield-quarter",
    title: "Security Settings",
    description: "Configure controls that protect PIERS users and information.",
    about:
      "Security settings control how PIERS protects your firm’s information. Review changes carefully and use your firm’s security policies when updating these controls.",
    capabilities: [
      "Find and filter users",
      "Create and update user access",
      "Reset passwords",
      "Suspend or block sign-in",
      "Review available roles",
    ],
    articles: firmAdministrationArticles,
    tourSteps: defaultTour,
  },
};

const PATH_CONTEXTS = [
  { match: (path) => path.startsWith("/firm/dashboard"), id: "firm.dashboard" },
  {
    match: (path) => path.startsWith("/system/checklist"),
    id: "admin.checklists",
  },
  {
    match: (path) =>
      path.startsWith("/system/standards") || path.startsWith("/piers/masters"),
    id: "admin.references",
  },
  {
    match: (path) => path.startsWith("/system/templates"),
    id: "admin.templates.review-template",
  },
  {
    match: (path) => path.startsWith("/inspectionreview"),
    id: "engagement.reviews",
  },
  { match: (path) => path.startsWith("/engagements/"), id: "engagement.list" },
  { match: (path) => path.startsWith("/engagement"), id: "engagement.details" },
  {
    match: (path) =>
      path.startsWith("/inspection") || path.startsWith("/review"),
    id: "review.workspace",
  },
  {
    match: (path) => path.startsWith("/report"),
    id: "reports.engagement-report",
  },
  {
    match: (path) =>
      path.startsWith("/settings/security") ||
      path.startsWith("/firm/settings"),
    id: "settings.security",
  },
];

export const getHelpContextForPath = (pathname = "") =>
  PATH_CONTEXTS.find(({ match }) => match(pathname))?.id || defaultContext.id;
export const getHelpContext = (id) => HELP_CONTEXTS[id] || defaultContext;

export const searchHelpContent = (query) => {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return [];

  return [defaultContext, helpCenterContext, ...Object.values(HELP_CONTEXTS)]
    .flatMap((context) =>
      context.articles.map((article) => ({ article, context }))
    )
    .filter(({ article, context }) =>
      `${context.title} ${context.description} ${article.title} ${
        article.group
      } ${article.body.join(" ")}`
        .toLowerCase()
        .includes(normalizedQuery)
    )
    .slice(0, 6);
};
